import { useCallback } from 'react';

import { useLocation } from 'react-router-dom';

import {
  defaultGroupings,
  defaultMetrics,
} from '../../../app/providers/redux/slices/reports/table/tableSlice';
import { defaultAttributes } from '../../../app/providers/redux/slices/reports/params/attributes/attrSlice';
import { PeriodInterval } from '../components/reports/params/period';
import { getAdGroupings } from '../../../helpers/utils/functions';
import { handleReportPeriod, parseTableData, migrateFiltersWithColor } from '../../../helpers/utils/reports';
import { normalizeSavedGroupsChecked, REPORT_LOADING_DELAYS } from '../../../helpers/utils/reportApiUtils';
import { normalizeMetricRefs } from '../components/reports/metrics/utils';
import { unApi } from '../api/endpoints/reports/unApi';
import { hasSavedSearchFilters, processSavedSearchFilters } from '../../../helpers/utils/savedSearchUtils';
import { getStartEndType } from '../components/reports/params/filter/pickers/DateTimeRangePicker';
import {
  listKeysGroupings,
  listKeysGroupingsAD,
  listKeysGroupingsOrder,
  listKeysGroupingsPayment,
  listKeysGroupingsWebinar,
  listKeysGroupingsVisit,
} from '../components/reports/modal-settings/index';
import { useActions } from './useActions';
import { useTimerCleanup } from './useTimerCleanup';
import { useTypedSelector } from './useTypedSelector';
import { devLog, devWarn } from '../../../helpers/utils/reportValidation';

/**
 * Интерфейс данных сохранённого отчёта.
 */
export interface ReportData {
  hash?: string;
  report_id?: number;
  id?: number;
  values: {
    name?: string;
    period?: {
      start: string;
      end: string;
      interval?: string;
      days?: { nStart: number; nEnd: number } | null;
    };
    details?: string;
    chartType?: string;
    groupings?: any[];
    metrics?: any[];
    groupsChecked?: any[];
    metricsChecked?: any[];
    filtersTable?: any[];
    sortTable?: any;
    attributes?: any;
    filtersReq?: any;
    filters?: any[];
    segment?: any;
    currentFunnel?: any[] | null;
    groupsSelectionMode?: 'manual' | 'all' | 'firstN';
    selectFirstNRows?: number | null;
    totalChecked?: any[];
    isChartVisible?: boolean;
    isAxesVisible?: boolean;
    cumulativeChart?: boolean;
    percentages?: string[];
    sparklines?: string[];
    cellFills?: Record<string, any>;
    frozensColumns?: Record<string, boolean>;
  };
}

/**
 * Интерфейс для метрики/группировки.
 */
export interface MetricOrGroupingItem {
  dataKey: string;
  name?: string;
  label?: string;
  [key: string]: unknown;
}

/**
 * Опции для хука useReportLoader.
 */
export interface UseReportLoaderOptions {
  /** Callback при начале загрузки */
  onLoadStart?: () => void;
  /** Callback при завершении загрузки */
  onLoadComplete?: () => void;
  /** Кастомные дефолтные метрики (если не указаны, используются из tableSlice) */
  customDefaultMetrics?: MetricOrGroupingItem[];
  /** Кастомные дефолтные группировки (если не указаны, используются из tableSlice) */
  customDefaultGroupings?: MetricOrGroupingItem[];
}

/**
 * Возвращаемые значения хука useReportLoader.
 */
export interface UseReportLoaderReturn {
  /** Загрузить сохранённый отчёт (асинхронно обрабатывает savedSearch фильтры) */
  loadReport: (report: ReportData) => Promise<void>;
  /** Загрузить настройки по умолчанию */
  loadDefaults: () => void;
}

/**
 * Возвращает список ключей группировок в зависимости от типа отчёта.
 * @param pathname - текущий путь (используется как fallback)
 * @param reportId - ID типа отчёта (1=регистрации, 2=заказы, 3=платежи, 4=реклама, 5=вебинары)
 */
const getGroupingsListKeysByPathname = (pathname: string, reportId?: number) => {
  // Если передан reportId, используем его для определения типа отчёта
  if (reportId !== undefined) {
    switch (reportId) {
      case 1: return listKeysGroupings; // Регистрации
      case 2: return listKeysGroupingsOrder; // Заказы
      case 3: return listKeysGroupingsPayment; // Платежи
      case 4: return listKeysGroupingsAD; // Реклама
      case 5: return listKeysGroupingsWebinar; // Вебинары
      default: break;
    }
  }
  
  // Fallback на pathname для новых отчётов
  if (pathname.includes('order-reports')) return listKeysGroupingsOrder;
  if (pathname.includes('payment-reports')) return listKeysGroupingsPayment;
  if (pathname.includes('ad-reports')) return listKeysGroupingsAD;
  if (pathname.includes('webinar-reports')) return listKeysGroupingsWebinar;
  if (pathname.includes('visit')) return listKeysGroupingsVisit;
  return listKeysGroupings;
};

/**
 * Хук для унифицированной загрузки отчётов.
 * 
 * Объединяет логику из:
 * - loadSavedReport() из Home.tsx
 * - manualActivateReport() из UniversalReportTitle.tsx
 * 
 * Применяет все 22 параметра отчёта:
 * 1) период - 2) тип регистрации - 3) атрибуция - 4) детализация
 * 5) воронка продаж - 6) сегменты - 7) набор фильтров
 * 8) показать/скрыть график - 9) показать/скрыть ось Y - 10) тип графика
 * 11) выбранные группировки - 12) выбранные метрики
 * 13) режим выбора группировок - 14) первые N строк
 * 15) итого и среднее - 16) выбранные чекбоксы строчек
 * 17) сортировка - 18) закрепление столбца - 19) фильтрация колонки
 * 20) процент в колонке - 21) выбранные метрики для графика - 22) накопительный график
 */
export const useReportLoader = (options?: UseReportLoaderOptions): UseReportLoaderReturn => {
  const location = useLocation();
  
  // Хук для безопасной работы с таймерами (автоочистка при размонтировании)
  const { safeSetTimeout } = useTimerCleanup();
  
  // Получаем ID школы для обработки savedSearch фильтров
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  
  const {
    setFilters,
    setMetrics,
    setGroupings,
    setFiltersTable,
    setCurrentReport,
    setCurrentSegment,
    setSortTable,
    setMetaCompare,
    setSeries,
    setMetricsChecked,
    setMetaChartFetching,
    setCurrentFunnel,
    setTableClearState,
    setTotalChecked,
    setDetails,
    setChartType,
    setAttributes,
    setPeriod,
    setGroupingsChecked,
    setGroupsSelectionMode,
    setSelectFirstNRows,
    clearTotalChecked,
    setMetaTableFetching,
    setAllDataFetching,
    setMetricsFiltersUpdate,
    setChartVisibleValue,
    setAxesVisibleValue,
    setComparePeriod,
    setCumulativeChart,
    setPercentages,
    setSparklines,
    setCellFills,
    setFrozensColumns,
    setMetaTableLoading,
    setMetaChartLoading,
  } = useActions();

  /**
   * Сбрасывает всё состояние перед загрузкой нового отчёта.
   */
  const resetState = useCallback(() => {
    // Сначала включаем лоадеры, чтобы пользователь не видел пустую таблицу
    setMetaTableLoading({ value: true });
    setMetaChartLoading({ value: true });
    // Затем сбрасываем данные
    setTableClearState();
    setMetaCompare({ value: false });
    setSeries([]);
  }, [setTableClearState, setMetaCompare, setSeries, setMetaTableLoading, setMetaChartLoading]);

  /**
   * Применяет основные параметры отчёта (синхронно).
   */
  const applyBaseParams = useCallback((params: {
    period?: { start: string; end: string; interval?: string; select?: string };
    details?: string;
    chartType?: string;
    groupings?: any[];
    metrics?: any[];
    attributes?: any;
    filters?: any[];
    currentSegment?: any;
    currentFunnel?: any[] | null;
    groupsSelectionMode?: 'manual' | 'all' | 'firstN';
    selectFirstNRows?: number | null;
    sortTable?: any;
    filtersTable?: any[];
    isChartVisible?: boolean;
    isAxesVisible?: boolean;
    cumulativeChart?: boolean;
    percentages?: string[];
    sparklines?: string[];
    cellFills?: Record<string, any>;
    frozensColumns?: Record<string, boolean>;
    reportId?: number; // ID типа отчёта для определения списка группировок
  }) => {
    // Используем reportId для определения списка группировок (приоритет над pathname)
    const groupingsListKeys = getGroupingsListKeysByPathname(location.pathname, params.reportId);
    
    // 1) Период
    if (params.period) {
      setPeriod({
        start: params.period.start,
        end: params.period.end,
        interval: (params.period.interval || 'selectdates') as PeriodInterval,
        select: params.period.select || '',
      });
      setComparePeriod({
        index: 2,
        period: {
          start: params.period.start,
          end: params.period.end,
          interval: params.period.interval || 'selectdates',
        },
      });
    }
    
    // 2-3) Тип регистрации и атрибуция
    if (params.attributes !== undefined) {
      setAttributes(params.attributes);
    }
    
    // 4) Детализация
    if (params.details !== undefined) {
      setDetails(params.details);
    }
    
    // 5) Воронка продаж
    setCurrentFunnel(params.currentFunnel ?? null);
    
    // 6) Сегменты
    setCurrentSegment(params.currentSegment ?? null);
    
    // 7) Фильтры
    if (params.currentSegment && params.filters) {
      setFilters([...(params.currentSegment.values?.filters || []), ...params.filters]);
    } else {
      setFilters(params.filters || []);
    }
    
    // 8) Видимость графика (по умолчанию true для старых отчётов)
    setChartVisibleValue(params.isChartVisible ?? true);
    
    // 9) Видимость оси Y (по умолчанию true для старых отчётов)
    setAxesVisibleValue(params.isAxesVisible ?? true);
    
    // 10) Тип графика
    if (params.chartType !== undefined) {
      setChartType(params.chartType);
    }
    
    // 11) Группировки
    devLog('[ReportLoader] applyBaseParams groupings:', {
      reportId: params.reportId,
      pathname: location.pathname,
      inputGroupings: params.groupings?.map((g: any) => g.dataKey),
      groupingsListKeysCount: groupingsListKeys.length,
    });
    
    if (params.groupings && params.groupings.length > 0) {
      const normalizedGroupings = getAdGroupings(params.groupings, groupingsListKeys);
      devLog('[ReportLoader] normalizedGroupings:', normalizedGroupings.map((g: any) => g.dataKey));
      
      // Если нормализация вернула пустой массив (группировки не найдены в актуальном списке),
      // используем дефолтные группировки, чтобы избежать зависания
      if (normalizedGroupings.length > 0) {
        setGroupings({ data: normalizedGroupings });
      } else {
        devWarn('[ReportLoader] Группировки не найдены в актуальном списке, используем дефолтные');
        setGroupings({ data: defaultGroupings });
      }
    } else {
      // Если группировки не переданы, используем дефолтные
      devWarn('[ReportLoader] Группировки не переданы, используем дефолтные');
      setGroupings({ data: defaultGroupings });
    }
    
    // 12) Метрики
    if (params.metrics && params.metrics.length > 0) {
      setMetrics({ data: params.metrics });
    } else {
      // Если метрики не переданы, используем дефолтные
      devWarn('[ReportLoader] Метрики не переданы, используем дефолтные');
      setMetrics({ data: defaultMetrics });
    }
    
    // 13) Режим выбора группировок
    setGroupsSelectionMode(params.groupsSelectionMode || 'manual');
    
    // 14) Первые N строк
    setSelectFirstNRows(params.selectFirstNRows ?? null);
    
    // 17) Сортировка
    setSortTable(params.sortTable || {});
    
    // 18) Закрепление столбцов
    setFrozensColumns(params.frozensColumns || {});
    
    // 19) Фильтрация колонок
    setFiltersTable(params.filtersTable || []);
    
    // 20) Процент в колонках
    setPercentages(params.percentages || []);

    // 21) Спарклайны в колонках
    setSparklines(params.sparklines || []);

    // 21а) Заливка ячеек обычных метрик по условиям «от – до»
    setCellFills(params.cellFills || {});
    
    // 22) Накопительный график
    setCumulativeChart(params.cumulativeChart ?? false);
  }, [
    location.pathname,
    setPeriod, setComparePeriod, setDetails, setChartType, setAttributes,
    setMetrics, setGroupings, setFiltersTable, setSortTable, setGroupsSelectionMode,
    setSelectFirstNRows, setCurrentSegment, setFilters, setCurrentFunnel,
    setChartVisibleValue, setAxesVisibleValue, setCumulativeChart, setPercentages, setSparklines, setCellFills, setFrozensColumns,
  ]);

  /**
   * Применяет отложенные параметры (после установки основных данных).
   * Использует safeSetTimeout для автоматической очистки при размонтировании.
   */
  const applyDeferredParams = useCallback((params: {
    totalChecked?: any[];
    groupsChecked?: any[];
    metricsChecked?: any[];
    groupsSelectionMode?: 'manual' | 'all' | 'firstN';
    metrics?: any[];
  }) => {
    safeSetTimeout(() => {
      // 15) Итого и среднее
      // Если totalChecked явно передан как пустой массив — очищаем
      // Если totalChecked undefined (старые отчёты) или содержит данные — включаем
      if (Array.isArray(params.totalChecked) && params.totalChecked.length === 0) {
        clearTotalChecked();
      } else {
        // По умолчанию включаем "Итого и среднее" (для старых отчётов и новых с галочкой)
        setTotalChecked();
      }
      
      // 16) Выбранные группировки (для ручного режима)
      if (params.groupsSelectionMode === 'manual' && params.groupsChecked && params.groupsChecked.length > 0) {
        setGroupingsChecked(params.groupsChecked);
      }
      
      // 21) Выбранные метрики для графика
      if (params.metricsChecked && params.metricsChecked.length > 0) {
        const enrichedMetricsChecked = normalizeMetricRefs(params.metricsChecked).map((savedMetric: any) => {
          const fullMetric = params.metrics?.find((m: any) => m.dataKey === savedMetric.dataKey);
          if (fullMetric && savedMetric.dataKey?.startsWith('_')) {
            return {
              ...savedMetric,
              unit: savedMetric.unit || fullMetric.unit,
              base_metric_name: savedMetric.base_metric_name || fullMetric.base_metric_name,
              metric_formula: savedMetric.metric_formula || fullMetric.metric_formula
            };
          }
          return savedMetric;
        });
        setMetricsChecked({ data: enrichedMetricsChecked });
      } else if (params.metrics && params.metrics.length > 0) {
        // Для старых отчётов без сохранённых metricsChecked — используем первую метрику
        const firstMetric = params.metrics[0];
        setMetricsChecked({
          data: [{
            dataKey: firstMetric.dataKey,
            name: firstMetric.dataKey,
            label: firstMetric.name || firstMetric.label,
          }]
        });
      }
    }, REPORT_LOADING_DELAYS.DEFERRED_PARAMS);
  }, [safeSetTimeout, setTotalChecked, clearTotalChecked, setGroupingsChecked, setMetricsChecked]);

  /**
   * Запускает загрузку данных (таблица и график).
   * Использует safeSetTimeout для автоматической очистки при размонтировании.
   * @param mode - 'all' для полной загрузки, 'chart' только для графика, 'table' только для таблицы
   * @param delay - задержка перед установкой флагов (по умолчанию REPORT_LOADING_DELAYS.DATA_FETCH)
   */
  const triggerDataFetch = useCallback((mode: 'all' | 'chart' | 'table' = 'all', delay = REPORT_LOADING_DELAYS.DATA_FETCH) => {
    const doFetch = () => {
      if (mode === 'chart') {
        setMetaChartFetching({ value: true });
      } else if (mode === 'table') {
        setMetaTableFetching({ value: true });
        setMetricsFiltersUpdate(true);
      } else {
        // mode === 'all' — загрузка и таблицы, и графика через один флаг
        setAllDataFetching({ value: true });
      }
    };
    
    if (delay > 0) {
      safeSetTimeout(doFetch, delay);
    } else {
      doFetch();
    }
  }, [safeSetTimeout, setMetaTableFetching, setMetricsFiltersUpdate, setAllDataFetching, setMetaChartFetching]);

  /**
   * Загружает сохранённый отчёт с сервера.
   * Асинхронно обрабатывает фильтры с savedSearch перед применением.
   */
  const loadReport = useCallback(async (report: ReportData) => {
    options?.onLoadStart?.();
    
    devLog('[ReportLoader] Загружаем сохранённый отчёт:', {
      report_id: report.report_id,
      hash: report.hash,
      name: report.values?.name,
      groupings: report.values?.groupings?.map((g: any) => g.dataKey),
      pathname: location.pathname,
    });
    
    // Сбрасываем состояние
    resetState();
    
    // Парсим данные отчёта
    let parsedMetrics = parseTableData(report.values.metrics || []);

    // Авто-фильтр: убираем кастомные метрики (_ID_), удалённые из системы,
    // чтобы «мёртвые» колонки не появлялись в отчёте после перезагрузки
    if (selectedSchool?.id) {
      try {
        const existingRes = await unApi.getCustomMetrics(selectedSchool.id, location.pathname);
        const existingTokens = new Set<string>(
          (Array.isArray(existingRes?.data) ? existingRes.data : [])
            .map((item: any) => item?.metric_name || (item?.id != null ? `_${item.id}_` : null))
            .filter(Boolean)
        );
        // Фильтруем только если справочник реально получен (не пустой) — иначе не трогаем
        if (existingTokens.size > 0) {
          parsedMetrics = parsedMetrics.filter((metric: any) => {
            const key = metric?.dataKey;
            // базовые метрики (не вида _ID_) оставляем всегда
            if (typeof key !== 'string' || !/^_\d+_$/.test(key)) return true;
            // кастомные — только если метрика ещё существует
            return existingTokens.has(key);
          });
        }
      } catch (e) {
        // справочник недоступен — не фильтруем (fail-safe)
      }
    }

    let parsedFilters = report.values.filters;
    if (!Array.isArray(parsedFilters)) {
      parsedFilters = [];
    }
    
    // Мигрируем старые фильтры: добавляем color если отсутствует
    parsedFilters = migrateFiltersWithColor(parsedFilters);

    // Пересчитываем даты для фильтров с пресетами (thismonth, today и т.д.)
    parsedFilters = parsedFilters.map((filter: any) => {
      if (filter.calendar?.type && filter.calendar.type !== 'selectdates') {
        const { start, end } = getStartEndType({ start: filter.start, end: filter.end, calendar: filter.calendar });
        if (start && end) {
          const hasTime = filter.calendar.time || filter.calendar.type === 'nhoursago' || (filter.calendar.type === 'ndaysago' && filter.calendar.nStartTime != null);
          const fmt = hasTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
          return { ...filter, start: start.format(fmt), end: end.format(fmt) };
        }
      }
      return filter;
    });

    const reportPeriod = report.values?.period || {};
    const handledPeriod = handleReportPeriod(reportPeriod);
    
    // Устанавливаем текущий отчёт
    setCurrentReport(report);
    
    // Обрабатываем фильтры с savedSearch
    // Если есть фильтры с сохранённым поиском, сначала загружаем значения по API
    if (hasSavedSearchFilters(parsedFilters) && selectedSchool?.id) {
      devLog('[ReportLoader] Обрабатываем фильтры с savedSearch...');
      try {
        parsedFilters = await processSavedSearchFilters(parsedFilters, selectedSchool.id);
        devLog('[ReportLoader] Фильтры с savedSearch обработаны:', parsedFilters);
      } catch (error) {
        devWarn('[ReportLoader] Ошибка обработки savedSearch фильтров:', error);
      }
    }
    
    // Обрабатываем сегмент
    const { segment } = report.values;
    const isSegment = segment && Object.keys(segment).length > 0;
    
    // Применяем основные параметры
    // Передаём report_id для корректного определения списка группировок
    applyBaseParams({
      period: handledPeriod,
      details: report.values.details || 'day',
      chartType: report.values.chartType || 'line',
      groupings: report.values.groupings,
      metrics: parsedMetrics,
      attributes: report.values?.filtersReq || report.values?.attributes || {},
      filters: parsedFilters,
      currentSegment: isSegment ? segment : null,
      currentFunnel: report.values?.currentFunnel || null,
      groupsSelectionMode: report.values?.groupsSelectionMode || 'manual',
      selectFirstNRows: report.values?.selectFirstNRows ?? null,
      sortTable: report.values.sortTable ?? {},
      filtersTable: report.values.filtersTable || [],
      isChartVisible: report.values?.isChartVisible ?? true,
      isAxesVisible: report.values?.isAxesVisible ?? true,
      cumulativeChart: report.values?.cumulativeChart ?? false,
      percentages: report.values?.percentages || [],
      sparklines: report.values?.sparklines || [],
      cellFills: report.values?.cellFills || {},
      frozensColumns: report.values?.frozensColumns || {},
      reportId: report.report_id, // ID типа отчёта для определения списка группировок
    });
    
    // Применяем отложенные параметры
    const savedGroupsSelectionMode = report.values?.groupsSelectionMode || 'manual';
    applyDeferredParams({
      totalChecked: report.values?.totalChecked,
      groupsChecked: savedGroupsSelectionMode === 'manual' 
        ? normalizeSavedGroupsChecked(report.values?.groupsChecked) 
        : [],
      metricsChecked: report.values?.metricsChecked,
      groupsSelectionMode: savedGroupsSelectionMode,
      metrics: parsedMetrics,
    });
    
    // Запускаем загрузку данных
    triggerDataFetch('all');
    
    // Вызываем callback завершения после запуска загрузки
    options?.onLoadComplete?.();
  }, [
    options, resetState, setCurrentReport, applyBaseParams,
    applyDeferredParams, normalizeSavedGroupsChecked, triggerDataFetch, location.pathname,
    selectedSchool,
  ]);

  /**
   * Загружает настройки по умолчанию для нового отчёта.
   * Использует кастомные дефолты из options, если они переданы.
   */
  const loadDefaults = useCallback(() => {
    options?.onLoadStart?.();
    
    // Используем кастомные дефолты если переданы, иначе — стандартные из tableSlice
    const metricsToUse = options?.customDefaultMetrics || defaultMetrics;
    const groupingsToUse = options?.customDefaultGroupings || defaultGroupings;
    
    devLog('[ReportLoader] Загружаем настройки по умолчанию:', {
      metrics: metricsToUse.map((m: MetricOrGroupingItem) => m.dataKey),
      groupings: groupingsToUse.map((g: MetricOrGroupingItem) => g.dataKey),
      isCustom: !!(options?.customDefaultMetrics || options?.customDefaultGroupings),
    });
    
    // Сбрасываем состояние (лоадеры уже включены в resetState)
    resetState();
    
    setCurrentReport({});
    
    // Применяем дефолтные параметры
    applyBaseParams({
      details: 'd',
      groupings: groupingsToUse,
      metrics: metricsToUse,
      attributes: defaultAttributes,
      filters: [],
      currentSegment: null,
      currentFunnel: null,
      groupsSelectionMode: 'manual',
      selectFirstNRows: null,
      sortTable: {},
      filtersTable: [],
      cumulativeChart: false,
      percentages: [],
      sparklines: [],
      cellFills: {},
      frozensColumns: {},
    });
    
    // Применяем отложенные параметры для дефолтного отчёта
    // Используем safeSetTimeout для автоматической очистки при размонтировании
    safeSetTimeout(() => {
      // 15) Итого и среднее (включаем по умолчанию)
      setTotalChecked();
      
      // 21) Выбранные метрики для графика
      const defaultMetricForChart = metricsToUse[0];
      if (defaultMetricForChart) {
        setMetricsChecked({
          data: [
            { 
              label: defaultMetricForChart.label, 
              name: defaultMetricForChart.dataKey,
              dataKey: defaultMetricForChart.dataKey,
            },
          ],
        });
      }
      
      // Запускаем загрузку данных после установки всех параметров
      // Задержка 0 — выполнится в следующем тике после установки metricsChecked
      triggerDataFetch('all', 0);
      
      // Вызываем callback завершения после запуска загрузки
      options?.onLoadComplete?.();
    }, REPORT_LOADING_DELAYS.DEFERRED_PARAMS);
  }, [
    safeSetTimeout, options, setCurrentReport, applyBaseParams,
    setTotalChecked, setMetricsChecked, triggerDataFetch,
    resetState,
  ]);

  return {
    loadReport,
    loadDefaults,
  };
};

export default useReportLoader;
